import { ConnectorSourceTransformationType } from '../types';
import { isEmptyString } from '@/utils/string';
import salesforceDefaultRenames from './salesforce/default-renames';
import hubspotDefaultRenames from './hubspot/default-renames';

interface RecordDescriptor {
  name: string;
  fields?: { name: string }[];
  labelPlural?: string;
}

export interface ComputedMappingDefaults {
  identifiers: Record<string, string>;
  renames: Record<string, string>;
  where_clause?: string;
  requiredFields: string[];
}

/** Case-insensitive lookups over a record's fields, shared by every branch. */
interface FieldLookup {
  fields: { name: string }[];
  /** The field's canonical name, or the search term itself when absent. */
  getField: (search: string) => string;
  hasField: (search: string) => boolean;
}

function makeFieldLookup(fields: { name: string }[]): FieldLookup {
  return {
    fields,
    getField: (search) => fields.find((f) => f.name.toLowerCase() === search.toLowerCase())?.name ?? search,
    hasField: (search) => fields.some((f) => f.name.toLowerCase() === search.toLowerCase()),
  };
}

/** The explicitly provided email field, or the record's own email-ish field. */
function resolveEmailField(fields: { name: string }[], emailField: string | undefined): string | null {
  if (!isEmptyString(emailField)) {
    return emailField;
  }
  return (
    fields.find(
      (f) =>
        f.name.toLowerCase() === 'email' ||
        f.name.toLowerCase() === 'properties_email' ||
        f.name.toLowerCase() === 'properties.email',
    )?.name ?? null
  );
}

function groupDefaults(record: RecordDescriptor, lookup: FieldLookup): ComputedMappingDefaults {
  const idFieldName = lookup.getField('Id');
  return {
    identifiers: { group_id: idFieldName, object_name: record.labelPlural ?? record.name },
    renames: {},
    requiredFields: [idFieldName],
  };
}

/**
 * For relationship records that have a Contact.Email field, default to using
 * it as the person ID and filter out records without an email; the first
 * non-Contact *.Id field becomes the object ID.
 */
function relationshipDefaults(
  record: RecordDescriptor,
  lookup: FieldLookup,
  isEmailEnabled: boolean,
): ComputedMappingDefaults {
  const defaults: ComputedMappingDefaults = {
    identifiers: { object_name: record.labelPlural ?? record.name },
    renames: {},
    requiredFields: [],
  };

  if (!isEmailEnabled || !lookup.hasField('Contact.Email')) {
    return defaults;
  }

  const contactEmailField = lookup.getField('Contact.Email');
  defaults.identifiers.user_id = contactEmailField;
  defaults.requiredFields.push(contactEmailField);
  defaults.where_clause = 'Contact.Email != null';

  const objectIdField = lookup.fields.find(
    (f) => f.name.toLowerCase().endsWith('.id') && !f.name.toLowerCase().startsWith('contact.'),
  );
  if (objectIdField != null) {
    defaults.identifiers.group_id = objectIdField.name;
    defaults.requiredFields.push(objectIdField.name);
  }

  return defaults;
}

/**
 * When email is enabled, use Email as Lead's default identifier so that when
 * a Lead converts to a Contact the email allows auto-merging the records in
 * Journeys while keeping the Lead Id as an attribute.
 */
function leadDefaults(
  lookup: FieldLookup,
  isEmailEnabled: boolean,
  resolvedEmailField: string,
): ComputedMappingDefaults {
  const idFieldName = lookup.getField('Id');

  if (!isEmailEnabled) {
    return { identifiers: { user_id: idFieldName }, renames: {}, requiredFields: [idFieldName] };
  }

  return {
    identifiers: { user_id: resolvedEmailField },
    renames: { [idFieldName]: 'lead_id' },
    requiredFields: [idFieldName, resolvedEmailField],
    where_clause: `${resolvedEmailField} != null`,
  };
}

/**
 * When email is enabled, use Email as the Contact identifier so profiles can
 * be merged in Journeys. Keep Id in fields as an attribute.
 */
function contactDefaults(lookup: FieldLookup, resolvedEmailField: string): ComputedMappingDefaults {
  const idFieldName = lookup.getField('Id');
  return {
    identifiers: { user_id: resolvedEmailField },
    renames: { [idFieldName]: 'contact_id' },
    requiredFields: [resolvedEmailField, idFieldName],
    where_clause: `${resolvedEmailField} != null`,
  };
}

function fallbackDefaults(lookup: FieldLookup): ComputedMappingDefaults {
  const idFieldName = lookup.getField('Id');
  return { identifiers: { user_id: idFieldName }, renames: {}, requiredFields: [idFieldName] };
}

/** Dispatches to the identifier defaults for the record's CDP type and shape. */
function identifierDefaults(
  record: RecordDescriptor,
  cdpType: string,
  lookup: FieldLookup,
  isEmailEnabled: boolean,
  resolvedEmailField: string | null,
): ComputedMappingDefaults {
  const recordType = record.name.toLowerCase();

  if (cdpType === ConnectorSourceTransformationType.GROUP) {
    return groupDefaults(record, lookup);
  }
  if (cdpType === ConnectorSourceTransformationType.RELATIONSHIP) {
    return relationshipDefaults(record, lookup, isEmailEnabled);
  }
  if ((recordType === 'lead' || recordType === 'leads') && resolvedEmailField != null) {
    return leadDefaults(lookup, isEmailEnabled, resolvedEmailField);
  }
  if ((recordType === 'contact' || recordType === 'contacts') && isEmailEnabled && resolvedEmailField != null) {
    return contactDefaults(lookup, resolvedEmailField);
  }
  return fallbackDefaults(lookup);
}

/** The source's static per-CDP-type rename table. */
function staticRenameTable(sourceOptionId: string, cdpType: string): Record<string, string> {
  if (sourceOptionId === 'hubspot') {
    return hubspotDefaultRenames[cdpType] ?? {};
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return (salesforceDefaultRenames as Record<string, Record<string, string>>)[cdpType] ?? {};
}

/**
 * The dynamic per-field rename rule, or null when none applies: HubSpot-style
 * `properties.` prefix stripping, and Salesforce's `Id` → `<Record>Id`. The
 * two rules are mutually exclusive by source.
 */
function dynamicFieldRename(sourceOptionId: string, record: RecordDescriptor, fieldName: string): string | null {
  if (sourceOptionId !== 'salesforce' && fieldName.startsWith('properties.')) {
    return fieldName.substring('properties.'.length);
  }
  if (sourceOptionId === 'salesforce' && fieldName === 'Id' && record.name !== '') {
    return `${record.name}Id`;
  }
  return null;
}

/**
 * Source-specific rename defaults, applied without overwriting branch-derived
 * renames: the static table first, then the dynamic per-field rules.
 */
function applyStaticRenames(
  renames: Record<string, string>,
  record: RecordDescriptor,
  cdpType: string,
  sourceOptionId: string,
  fields: { name: string }[],
): void {
  for (const [key, value] of Object.entries(staticRenameTable(sourceOptionId, cdpType))) {
    if (!(key in renames)) {
      renames[key] = value;
    }
  }

  for (const field of fields) {
    const rename = dynamicFieldRename(sourceOptionId, record, field.name);
    if (rename != null && !(field.name in renames)) {
      renames[field.name] = rename;
    }
  }
}

/**
 * Computes default identifiers, renames, where_clause, and required fields
 * for a given record descriptor and CDP type. Shared between the preview
 * drawer (paved road) and the full sync editor (defaultMapping).
 *
 * When emailField is provided (from sources.yaml sync templates), it is
 * used directly. Otherwise falls back to searching the record's fields.
 */
export function computeMappingDefaults(
  record: RecordDescriptor,
  cdpType: string,
  isEmailEnabled: boolean,
  emailField?: string,
  sourceOptionId?: string,
): ComputedMappingDefaults {
  // A field-less record can reach here (e.g. a fresh sync on a disconnected
  // source whose record-type queries are gated off), so default to an empty
  // list rather than assuming `fields` is always populated.
  const fields = record.fields ?? [];
  const lookup = makeFieldLookup(fields);
  const resolvedEmailField = resolveEmailField(fields, emailField);

  const defaults = identifierDefaults(record, cdpType, lookup, isEmailEnabled, resolvedEmailField);

  if (sourceOptionId != null) {
    applyStaticRenames(defaults.renames, record, cdpType, sourceOptionId, fields);
  }

  return defaults;
}
